import { useState } from 'react'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import { useIsMobile } from '../hooks/useIsMobile'
import XAxisModal from './XAxisModal'
import YAxisModal from './YAxisModal'
import './Toolbar.css'

export default function Toolbar() {
  const { project, addFlow, toggleFlowVisibility, timeView, setTimeView, zoom, setZoom, activeView, setActiveView } = useProject()
  const { t, language } = useI18n()
  const isMobile = useIsMobile()
  const [xAxisOpen, setXAxisOpen] = useState(false)
  const [yAxisOpen, setYAxisOpen] = useState(false)

  const handleAddFlow = () => {
    // Extended color palette for flows (20 distinct colors)
    const colors = [
      '#E85D04', // Orange
      '#1D3557', // Navy Blue
      '#2A9D8F', // Teal
      '#9B5DE5', // Purple
      '#118AB2', // Ocean Blue
      '#06D6A0', // Mint Green
      '#EF476F', // Pink/Red
      '#FFD166', // Yellow
      '#F72585', // Magenta
      '#4361EE', // Royal Blue
      '#3A86FF', // Bright Blue
      '#06FFA5', // Neon Green
      '#FF6B35', // Coral
      '#A83279', // Deep Pink
      '#5F0F40', // Burgundy
      '#0EAD69', // Emerald
      '#7209B7', // Violet
      '#F28482', // Salmon
      '#84A59D', // Sage Green
      '#F5B700'  // Gold
    ]
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    const flowNumber = project.flows.length + 1
    addFlow(`${t('flow.newFlow')} ${flowNumber}`, randomColor)
  }

  const handleZoomChange = (e) => {
    setZoom(parseInt(e.target.value))
  }

  const isNorwegian = language === 'nb'

  return (
    <>
      {/* ── Main toolbar ── */}
      <div className="toolbar">
        <div className="toolbar-group">
          <div className="view-toggle">
            <button
              className={`view-btn ${activeView === 'process' ? 'active' : ''}`}
              onClick={() => setActiveView('process')}
            >
              {isNorwegian ? 'Prosess' : 'Process'}
            </button>
            <button
              className={`view-btn ${activeView === 'dependencies' ? 'active' : ''}`}
              onClick={() => setActiveView('dependencies')}
            >
              {isNorwegian ? 'Avhengigheter' : 'Deps'}
            </button>
            <button
              className={`view-btn ${activeView === 'description' ? 'active' : ''}`}
              onClick={() => setActiveView('description')}
            >
              {isNorwegian ? 'Beskrivelse' : 'Overview'}
            </button>
          </div>
        </div>

        {activeView === 'process' && (
          <>
            <div className="toolbar-divider" />

            <div className="toolbar-group">
              <button className="toolbar-btn primary" onClick={handleAddFlow}>
                <span className="icon">+</span>
                <span className="btn-text">{t('toolbar.addFlow')}</span>
              </button>

              {!isMobile && (
                <>
                  <button
                    className="toolbar-btn"
                    onClick={() => setXAxisOpen(true)}
                    title={isNorwegian ? 'Konfigurer X-akse' : 'Configure X-axis'}
                  >
                    <span className="icon">☰</span>
                    <span className="btn-text">{isNorwegian ? 'X-akse' : 'X-axis'}</span>
                  </button>

                  <button
                    className="toolbar-btn"
                    onClick={() => setYAxisOpen(true)}
                    title={isNorwegian ? 'Konfigurer Y-akse' : 'Configure Y-axis'}
                  >
                    <span className="icon">≡</span>
                    <span className="btn-text">{isNorwegian ? 'Y-akse' : 'Y-axis'}</span>
                  </button>
                </>
              )}
            </div>

            <div className="toolbar-divider" />

            <div className="toolbar-group">
              <div className="view-toggle">
                <button
                  className={`view-btn icon-only ${timeView === 'compact' ? 'active' : ''}`}
                  onClick={() => setTimeView('compact')}
                  title={isNorwegian ? 'Kompakt visning' : 'Compact view'}
                >
                  ▣
                </button>
                <button
                  className={`view-btn icon-only ${timeView === 'proportional' ? 'active' : ''}`}
                  onClick={() => setTimeView('proportional')}
                  title={isNorwegian ? 'Tidsproporsjonal' : 'Time-proportional'}
                >
                  ◧
                </button>
              </div>

              <div className="zoom-control">
                <span className="zoom-icon">🔍</span>
                <input
                  type="range"
                  className="zoom-slider"
                  min="50"
                  max="200"
                  value={zoom}
                  onChange={handleZoomChange}
                />
                <span className="zoom-value">{zoom}%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Mobile flow strip (only visible on small screens in process view) ── */}
      {activeView === 'process' && project.flows.length > 0 && (
        <div className="mobile-flow-strip">
          {project.flows.map(flow => (
            <button
              key={flow.id}
              className={`mobile-flow-chip ${flow.hidden ? 'hidden' : ''}`}
              onClick={() => toggleFlowVisibility(flow.id)}
              style={{ '--flow-color': flow.color }}
            >
              <span className="mobile-flow-dot" style={{ backgroundColor: flow.color }} />
              <span className="mobile-flow-name">{flow.name}</span>
              <span className="mobile-flow-eye">{flow.hidden ? '🙈' : '👁'}</span>
            </button>
          ))}
        </div>
      )}

      <XAxisModal isOpen={xAxisOpen} onClose={() => setXAxisOpen(false)} />
      <YAxisModal isOpen={yAxisOpen} onClose={() => setYAxisOpen(false)} />
    </>
  )
}
